// Workspace-internal governance bridge. Wires the abstract sdk-bridge +
// sdk-supabase-bridge primitives into a project-specific federation for
// the governance Supabase project — the workspace shell's only auth
// boundary.
//
// FINANCIALS IS NOT WIRED HERE. The workspace bundle never touches the
// financials project. Apps that need financials (admin consoles, finance
// dashboards) wire their own bridge in their own module — see
// `governance/packages/finance-features/src/bridge.js`.
//
// What this module exposes (legacy surface preserved so the ~30
// consumer call sites don't have to change semantics):
//   getClient / getClientAsync / forEachClient
//   loginAll / logoutAll / switchWorkspace / refreshClaims /
//   activateExtension / getSymbolsToken / getSessionStatus
//   getSupabase / getSupabaseAsync / getRealtimeSupabase  (legacy aliases)
//   configure / getAppConfig / getRegistry / getProjectConfig /
//   listConfiguredProjects / resetRegistry
//   PROJECT_GOVERNANCE  (string constant)
//   hydrateAuthFromCookie / hydrateAuthFromIframeBridge /
//   persistAuthToCookie / clearAuthCookie / installAuthSync /
//   persistSupabaseSessionsToCookie / hydrateSupabaseSessionsFromCookie /
//   collectSdkTokens / collectSupabaseSessions / TOKEN_KEYS / SUPABASE_KEY_RE

import { createRegistry } from '@symbo.ls/sdk-bridge'
import {
  createSupabaseClient,
  createAuthBridge,
  createCrossAppAuth,
  buildRegistryConfig,
  createAppConfig
} from '@symbo.ls/sdk-supabase-bridge'

export const PROJECT_GOVERNANCE = 'governance'

// Hardcoded fallback creds — the governance Supabase project the
// workspace has always pointed at. Kept so first-deploy boots without
// requiring SYMBOLS_APP_GOVERNANCE_SUPABASE_* env vars. Remove once
// every workspace build supplies env vars consistently.
const GOVERNANCE_FALLBACK = {
  url: 'https://becrzpqaiovbvfmrosro.supabase.co',
  anonKey: 'sb_publishable_suMZ-1Crw-304sk_MY-VZA_8aU6ZFfa',
  anonJwt: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJlY3J6cHFhaW92YnZmbXJvc3JvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU2MzE1NzAsImV4cCI6MjA5MTIwNzU3MH0.0vvvM-z9MJ2Zl2AhLBS-7gZ_eEci-GRDrFaFrdWD41U'
}

const _descriptors = [
  {
    key: PROJECT_GOVERNANCE,
    opts: {
      required: true,
      fallback: GOVERNANCE_FALLBACK,
      accessTokenTTL: 600,
      // Skip governance for users with only a personal org — landing on
      // a governance session with no team-org is pure cold-start waste.
      // Apps that want governance unconditionally pass
      // configure({ required: ['governance'] }) to bypass shouldActivate.
      shouldActivate: (claims) => {
        const orgs = Array.isArray(claims?.orgs) ? claims.orgs : []
        if (!orgs.length) return false
        const hasType = orgs.some((o) => o.type != null)
        if (!hasType) return true
        return orgs.some((o) => o.type === 'team')
      }
    }
  }
]

let _registry = null
let _registryConfig = null
let _auth = null
let _appConfig = null

const _ensure = () => {
  if (_registry) return
  _registryConfig = buildRegistryConfig(_descriptors)
  _registry = createRegistry({
    projects: _registryConfig,
    defaultKey: PROJECT_GOVERNANCE,
    buildClient: createSupabaseClient
  })
  _appConfig = createAppConfig(() => _registryConfig)
  _auth = createAuthBridge({ registry: _registry, appConfig: _appConfig })
}

// ── Registry / project config ────────────────────────────────────────

export const getClient = (key = PROJECT_GOVERNANCE) => {
  _ensure()
  return _registry.getClient(key)
}
export const getClientAsync = async (key) => getClient(key)
export const forEachClient = (fn) => {
  _ensure()
  return _registry.forEachClient(fn)
}
export const _resetClients = () => {
  _ensure()
  return _registry.reset()
}

export const getRegistry = () => { _ensure(); return _registryConfig }
export const getProjectConfig = (key) => {
  _ensure()
  const cfg = _registryConfig[key]
  if (!cfg && key === PROJECT_GOVERNANCE) {
    throw new Error('[governanceBridge] governance project not configured')
  }
  return cfg || null
}
export const listConfiguredProjects = () => {
  _ensure()
  return Object.keys(_registryConfig)
}
export const resetRegistry = () => {
  _registry = null
  _registryConfig = null
  _auth = null
  _appConfig = null
}

// ── App config ───────────────────────────────────────────────────────

export const configure = (opts) => { _ensure(); _appConfig.configure(opts) }
export const getAppConfig = () => { _ensure(); return _appConfig.get() }

// ── Auth bridge ──────────────────────────────────────────────────────

export const loginAll = (args) => { _ensure(); return _auth.loginAll(args) }
export const logoutAll = () => { _ensure(); return _auth.logoutAll() }
export const switchWorkspace = (id) => { _ensure(); return _auth.switchWorkspace(id) }
export const refreshClaims = () => { _ensure(); return _auth.refreshClaims() }
export const activateExtension = (k, a) => { _ensure(); return _auth.activateExtension(k, a) }
export const getSymbolsToken = () => { _ensure(); return _auth.getSymbolsToken() }
export const getSessionStatus = () => { _ensure(); return _auth.getSessionStatus() }

// Legacy aliases — pre-federation callers used a single `getSupabase()`.
export const getSupabase = () => getClient(PROJECT_GOVERNANCE)
export const getSupabaseAsync = async () => getClient(PROJECT_GOVERNANCE)
export const getRealtimeSupabase = getSupabaseAsync

// ── Cross-app auth (canvas ↔ workspace cookie + iframe sync) ─────────

// Peer resolution: cookies with domain=.localhost are silently dropped
// by Chrome, so the cookie strategy only works on prod .symbols.app.
// In dev we iframe the peer's `/_session-bridge` route.
function peerOrigin () {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname
  const isCanvas = host.startsWith('canvas.') || host === 'edit.symbols.app'
  const isWorkspace = host.startsWith('workspace.') ||
    host.startsWith('system-governance-intranet.') ||
    host.startsWith('governance-intranet.') ||
    host === 'us-at.symbols.app'
  if (host.endsWith('.localhost') || host === 'localhost') {
    if (isCanvas) return 'http://workspace.localhost:1355'
    if (isWorkspace) return 'http://canvas.localhost:1355'
    return null
  }
  if (host.endsWith('.symbols.app')) {
    if (isCanvas) return 'https://us-at.symbols.app'
    if (isWorkspace) return 'https://edit.symbols.app'
    return null
  }
  return null
}

const _crossApp = createCrossAppAuth({ resolvePeerOrigin: peerOrigin })

export const TOKEN_KEYS = _crossApp.TOKEN_KEYS
export const SUPABASE_KEY_RE = _crossApp.SUPABASE_KEY_RE
export const hydrateAuthFromCookie = _crossApp.hydrateAuthFromCookie
export const hydrateAuthFromIframeBridge = _crossApp.hydrateAuthFromIframeBridge
export const persistAuthToCookie = _crossApp.persistAuthToCookie
export const clearAuthCookie = _crossApp.clearAuthCookie
export const installAuthSync = _crossApp.installAuthSync
export const persistSupabaseSessionsToCookie = _crossApp.persistSupabaseSessionsToCookie
export const hydrateSupabaseSessionsFromCookie = _crossApp.hydrateSupabaseSessionsFromCookie
export const collectSdkTokens = _crossApp.collectSdkTokens
export const collectSupabaseSessions = _crossApp.collectSupabaseSessions

// ── Marketplace dynamic-import stubs ─────────────────────────────────
//
// Marketplace's `import('...').then(mod => mod.attachLibrary?.(...))`
// pattern requires these exports to exist (Parcel's static export
// analysis errors otherwise). They're null until the corresponding
// endpoints ship. The marketplace existence-check (`if (mod.fn) ...`)
// falls back gracefully meanwhile.
export const installPlugin = null
export const scaffoldFromTemplate = null
export const saveMcpConnector = null
export const attachLibrary = null
